import * as React from "react"
import { Button as BaseButton } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useButtonGroupDefaults } from "@/components/ui/button-group"

const buttonVariants = cva(
  "duration-btn inline-flex cursor-pointer items-center justify-center font-semibold transition-[opacity,transform] ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:scale-[var(--btn-focus-scale)] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-muted text-foreground hover:opacity-90",
        primary: "bg-primary text-primary-foreground hover:opacity-90",
        secondary: "bg-secondary text-secondary-foreground hover:opacity-90",
        accent: "bg-accent text-accent-foreground hover:opacity-90",
        neutral: "bg-neutral text-neutral-foreground hover:opacity-90",
        tertiary: "bg-tertiary text-tertiary-foreground hover:opacity-90",
        destructive:
          "bg-destructive text-destructive-foreground hover:opacity-90",
        info: "bg-info text-info-foreground hover:opacity-90",
        success: "bg-success text-success-foreground hover:opacity-90",
        warning: "bg-warning text-warning-foreground hover:opacity-90",
        error: "bg-destructive text-destructive-foreground hover:opacity-90",
        outline:
          "border-1 border-primary bg-transparent text-primary hover:bg-primary/10",
        ghost: "bg-transparent text-foreground hover:bg-muted",
        link: "bg-transparent text-primary underline-offset-4 hover:underline",
      },
      variantStyle: {
        fill: "",
        outline: "border-1 bg-transparent hover:bg-current/10",
      },
      size: {
        xs: "h-6 gap-1 px-2 text-xs [&>svg]:size-3",
        sm: "h-8 gap-1.5 px-3 text-sm [&>svg]:size-3.5",
        md: "h-10 gap-2 px-4 text-base [&>svg]:size-4",
        lg: "h-12 gap-2 px-5 text-lg [&>svg]:size-4.5",
        xl: "h-14 gap-2 px-6 text-lg [&>svg]:size-5",
        icon: "size-10 p-0 [&>svg]:size-6",
        "icon-xs": "size-6 p-0 [&>svg]:size-4",
        "icon-sm": "size-8 p-0 [&>svg]:size-4.5",
        "icon-lg": "size-12 p-0 [&>svg]:size-7",
        "icon-xl": "size-14 p-0 [&>svg]:size-8",
      },
      radius: {
        square: "rounded-lg",
        circle: "rounded-full",
      },
    },
    compoundVariants: [
      {
        variant: [
          "default",
          "primary",
          "secondary",
          "accent",
          "neutral",
          "tertiary",
          "destructive",
          "info",
          "success",
          "warning",
          "error",
        ],
        variantStyle: "fill",
        className: "border-1 border-transparent",
      },
      {
        variant: "default",
        variantStyle: "outline",
        className: "border-border text-foreground hover:bg-muted",
      },
      {
        variant: "primary",
        variantStyle: "outline",
        className: "border-primary text-primary hover:bg-primary/10",
      },
      {
        variant: "secondary",
        variantStyle: "outline",
        className: "border-secondary text-secondary hover:bg-secondary/10",
      },
      {
        variant: "accent",
        variantStyle: "outline",
        className: "border-accent text-accent hover:bg-accent/10",
      },
      {
        variant: "neutral",
        variantStyle: "outline",
        className: "border-neutral text-neutral hover:bg-neutral/10",
      },
      {
        variant: "info",
        variantStyle: "outline",
        className: "border-info text-info hover:bg-info/10",
      },
      {
        variant: "success",
        variantStyle: "outline",
        className: "border-success text-success hover:bg-success/10",
      },
      {
        variant: "warning",
        variantStyle: "outline",
        className: "border-warning text-warning hover:bg-warning/10",
      },
      {
        variant: "error",
        variantStyle: "outline",
        className:
          "border-destructive text-destructive hover:bg-destructive/10",
      },
      {
        variant: "destructive",
        variantStyle: "outline",
        className:
          "border-destructive text-destructive hover:bg-destructive/10",
      },
    ],
    defaultVariants: {
      variant: "default",
      variantStyle: "fill",
      size: "md",
      radius: "square",
    },
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>

export interface ButtonProps
  extends React.ComponentPropsWithoutRef<typeof BaseButton>, ButtonVariants {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, variantStyle, size, radius, ...props }, ref) => {
    const groupDefaults = useButtonGroupDefaults()
    return (
      <BaseButton
        ref={ref as React.Ref<HTMLElement>}
        data-slot="button"
        className={cn(
          buttonVariants({
            variant: variant ?? groupDefaults?.variant,
            variantStyle,
            size: size ?? groupDefaults?.size,
            radius,
            className,
          })
        )}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
